import { useEffect, useState } from 'react'
import { createSelector, unwrapResult } from '@reduxjs/toolkit'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import {
  accountLookup,
  AuthenticationState,
  check2faCode,
  check2faDob,
  CheckoutAppSelector,
  get2faCode,
  smsVerificationEnabledSelector,
  useAppDispatch,
  useAppSelector,
  useConsumerSelector,
  useSelectedPaymentMethod,
  useTraceId,
  verifyEfaCode,
  getEfaCode,
  setEFAFinished,
  useConfirmCheckout,
  useIsApplePaylater,
  resetCheckoutError,
  setCheckoutError,
  PersistentStepUpReason,
} from '../../'
import { Route, usePostLoginRoute } from '../../../config/router'
import {
  Authentication,
  AuthenticationErrors,
  FingerPrint,
  MobileLogin,
  VerificationError,
  VerificationErrorCode,
  VerificationFlow,
  VerificationFlowContext,
  VerificationMethod,
  VerificationFlowRoute,
  LoginAttemptEventParams,
  PaymentMethodType,
  LoginMethodTypes,
  EFA_ERRORS,
  EFA_CHECK_STATUS,
} from '../../../model'
import { useCheckoutError, useCheckoutToken, useStartCheckoutLoginMethod } from '../../checkout/hooks'
import { useConsumerFeatureFlagReady } from '../../consumer/hooks'
import { useMonitoringWithEnduringEventProperties } from '../../../integrations/monitoring'
import { TrackingEvent } from '../../../model/monitoring'
import { getPaymentMethodId } from './helper'
import { markInteractiveLogin } from '../../../flow/remember-me/loginSignal'
import { authService } from '@afterpaytouch/portal-api/consumer/auth'
import { useFinaliseApplePaylaterCheckout } from '../../../flow/apple-paylater/hooks'

export const authenticationStateSelector: CheckoutAppSelector<AuthenticationState> = (state) => state.auth?.user ?? {}

export const fingerprintSelector: CheckoutAppSelector<FingerPrint | null> = createSelector(authenticationStateSelector, (state) => state.fingerprint)

export const fingerprintHeaderValue: CheckoutAppSelector<string | undefined> = createSelector(fingerprintSelector, (fingerprint) =>
  fingerprint !== null ? JSON.stringify(fingerprint) : undefined
)

export const profilingIdSelector: CheckoutAppSelector<string> = createSelector(authenticationStateSelector, (state) => state?.profilingId ?? '')

export const authenticationSelector: CheckoutAppSelector<Authentication> = createSelector(authenticationStateSelector, (state) => state.status)

export const authenticationErrorSelector: CheckoutAppSelector<Record<string, AuthenticationErrors | undefined>> = createSelector(
  authenticationStateSelector,
  (state) => ({
    error: state.error,
  })
)

export const enableOTPSelector: CheckoutAppSelector<Record<string, boolean>> = createSelector(authenticationStateSelector, (state) => ({
  enableOTP: state.enableOTP,
}))

export const autoSendOTPSelector: CheckoutAppSelector<boolean> = createSelector(authenticationStateSelector, (state) => state.autoSendOTP)

export const enabled2faMethodsSelector: CheckoutAppSelector<Record<VerificationMethod, boolean>> = createSelector(
  authenticationStateSelector,
  (state) => state.enabled2faMethods
)

export const initial2faMethodSelector: CheckoutAppSelector<VerificationMethod | undefined> = createSelector(
  authenticationStateSelector,
  (state) => state.initial2faMethod
)

export const autoSend2faSelector: CheckoutAppSelector<boolean> = createSelector(authenticationStateSelector, (state) => state.autoSend2fa)

export const isTrustedCheckoutSelector: CheckoutAppSelector<boolean> = createSelector(authenticationStateSelector, (state) => state.isTrustedCheckout)

export const mobileLoginSelector: CheckoutAppSelector<MobileLogin> = createSelector(
  authenticationStateSelector,
  (state) => state.mobileLogin ?? { isMobileLogin: false, dateOfBirthVerificationRequired: false, hasSeenChallenge: false }
)

export const persistentAndOptedInSelector: CheckoutAppSelector<boolean> = createSelector(authenticationStateSelector, (state) => {
  const { persistent = false, persistentOptIn = false } = state.status
  return persistent && !persistentOptIn
})

export const recognizedViaPersistentLoginSelector: CheckoutAppSelector<boolean> = createSelector(
  authenticationStateSelector,
  (state) => state.recognizedViaPersistentLogin
)

export const autoRememberMeStepUpSelector: CheckoutAppSelector<boolean> = createSelector(authenticationStateSelector, (state) => state.autoRememberMeStepUp)

export const autoRememberMeStepUpReasonSelector: CheckoutAppSelector<PersistentStepUpReason | undefined> = createSelector(
  authenticationStateSelector,
  (state) => state.autoRememberMeStepUpReason
)

export const authTokenSelector: CheckoutAppSelector<string | undefined> = createSelector(authenticationStateSelector, (state) => state.authToken)

export const afterpayCheckoutLoginTokenSelector: CheckoutAppSelector<string | undefined> = createSelector(
  authenticationStateSelector,
  (state) => state.afterpayCheckoutLoginToken
)

export const cashCheckoutTokenSelector: CheckoutAppSelector<string | null> = createSelector(
  authenticationStateSelector,
  (state) => state.cashCheckoutToken ?? null
)

export const isPrivateBrowsingModeSelector: CheckoutAppSelector<boolean> = createSelector(authenticationStateSelector, (state) => state.isPrivateBrowsingMode)

export const useProfilingId = (): string => useAppSelector(profilingIdSelector)

export const useAuthenticationState = (): AuthenticationState => useAppSelector(authenticationStateSelector)

export const useAuthentication = (): Authentication => useAppSelector(authenticationSelector)

export const useAuthenticationErrors = (): Record<string, AuthenticationErrors | undefined> => useAppSelector(authenticationErrorSelector)

export const useEnableOTP = (): { enableOTP: boolean } => {
  const { enableOTP } = useAppSelector(enableOTPSelector)
  const { smsVerificationEnabled } = useAppSelector(smsVerificationEnabledSelector)
  return { enableOTP: smsVerificationEnabled && enableOTP }
}

export const useAutoSendOTP = (): boolean => useAppSelector(autoSendOTPSelector)

export const useEnabled2faMethods = (): Record<VerificationMethod, boolean> => useAppSelector(enabled2faMethodsSelector)

export const useInitial2faMethod = (): VerificationMethod | undefined => useAppSelector(initial2faMethodSelector)

export const useAutoSend2fa = (): boolean => useAppSelector(autoSend2faSelector)

export const useIsTrustedCheckout = (): boolean => useAppSelector(isTrustedCheckoutSelector)

export const useMobileLogin = (): MobileLogin => useAppSelector(mobileLoginSelector)

export const useIsPersistentAndOptedIn = (): boolean => useAppSelector(persistentAndOptedInSelector)

export const useIsRecognizedViaPersistentLogin = (): boolean => useAppSelector(recognizedViaPersistentLoginSelector)

export const useIsAutoRememberMeStepUp = (): boolean => {
  const autoRememberMeStepUp = useAppSelector(autoRememberMeStepUpSelector)
  const loginMethod = useStartCheckoutLoginMethod()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  return autoRememberMeStepUp && hasFeatureFlagsReady && loginMethod === 'Auto Remember Me'
}

export const useIsAutoRememberMeStepUpInFlight = (): boolean => useAppSelector(autoRememberMeStepUpSelector)

export const useIsStepUpIdentityUntrusted = (): boolean => {
  const autoRememberMeStepUp = useAppSelector(autoRememberMeStepUpSelector)
  const loginMethod = useStartCheckoutLoginMethod()
  const untrusted = autoRememberMeStepUp && loginMethod !== 'Trusted Checkout (OAuth)'
  const [latched, setLatched] = useState(untrusted)
  if (untrusted && !latched) {
    setLatched(true)
  }
  return latched
}

const STEP_UP_ELIGIBILITY_MAX_WAIT_MS = 5000

export type AutoRememberMeStepUpEligibility = 'deciding' | 'undecided' | 'decided'

export const useAutoRememberMeStepUpEligibility = (): AutoRememberMeStepUpEligibility => {
  const autoRememberMeStepUp = useAppSelector(autoRememberMeStepUpSelector)
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const loginMethod = useStartCheckoutLoginMethod()
  const [eligibilityWaitExpired, setEligibilityWaitExpired] = useState(false)

  const isUndecidable = autoRememberMeStepUp && !hasFeatureFlagsReady && loginMethod !== 'Trusted Checkout (OAuth)'
  const shouldHold = isUndecidable && !eligibilityWaitExpired

  useEffect(() => {
    if (!shouldHold) {
      return
    }
    const timer = setTimeout(() => setEligibilityWaitExpired(true), STEP_UP_ELIGIBILITY_MAX_WAIT_MS)
    return () => clearTimeout(timer)
  }, [shouldHold])

  if (!isUndecidable) {
    return 'decided'
  }
  return eligibilityWaitExpired ? 'undecided' : 'deciding'
}

export const useAutoRememberMeStepUpReason = (): PersistentStepUpReason | undefined => useAppSelector(autoRememberMeStepUpReasonSelector)

export const useIsPrivateBrowsingMode = (): boolean => useAppSelector(isPrivateBrowsingModeSelector)

export const useFingerprint = (): FingerPrint | null => useAppSelector(fingerprintSelector)

const SECURITY_2FA_ERRORS: Partial<Record<ErrorCode, VerificationErrorCode>> = {
  [ErrorCode.SmsCodeNotFound]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.SmsMessagingNotEnabled]: VerificationErrorCode.MethodNotEnabled,
  [ErrorCode.SmsVerificationCodeGenerationMaximumReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.VerificationCodeGenerationNotAllowed]: VerificationErrorCode.MethodNotEnabled,
  [ErrorCode.SmsVerificationCodeValidationMaximumReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.InvalidSmsCode]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.MaxSmsCodeAttempts]: VerificationErrorCode.MaxSmsCodeAttempts,
  [ErrorCode.MaxAttemptsReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.InvalidDobVerification]: VerificationErrorCode.InvalidDob,
  [ErrorCode.MaxDobVerificationAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxEmailCodeAttempts]: VerificationErrorCode.MaxEmailCodeAttempts,
} as const

export const useSecurity2faFlow = (): VerificationFlow => {
  const dispatch = useAppDispatch()
  const enabled = useEnabled2faMethods()
  const initialMethod = useInitial2faMethod()
  const autoSendCode = useAutoSend2fa()
  const { mobile } = useConsumerSelector()
  const postLoginRoute = usePostLoginRoute()

  const sendCode = async (method: VerificationMethod): Promise<void> => {
    try {
      unwrapResult(await dispatch(get2faCode({ sendBy: method })))
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(SECURITY_2FA_ERRORS[errorCode])
    }
  }
  const checkCode = async (code: string): Promise<VerificationFlowRoute> => {
    try {
      unwrapResult(await dispatch(check2faCode({ code })))
      const account = unwrapResult(await dispatch(accountLookup()))
      if (postLoginRoute === Route.LOGGED_IN) {
        markInteractiveLogin()
      }
      return {
        route: postLoginRoute,
        locale: account.preferredLocale,
      }
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(SECURITY_2FA_ERRORS[errorCode])
    }
  }

  const checkDob = async (dateOfBirth: Date, lastName: string): Promise<void> => {
    try {
      unwrapResult(
        await dispatch(
          check2faDob({
            mobile: mobile ?? '',
            dateOfBirth,
            lastName,
          })
        )
      )
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(SECURITY_2FA_ERRORS[errorCode])
    }
  }
  return {
    enabled,
    sendCode,
    checkCode,
    autoSendCode,
    initialMethod,
    context: VerificationFlowContext.Security,
    allowLogout: true,
    revealMobile: false,
    previousRoute: Route.ENTER_PASSWORD,
    prefetchRoutes: [Route.ENTER_PASSWORD, Route.LOGGED_IN],
    checkDob,
  }
}

export const useCheckoutConfirm2faFlow = (): VerificationFlow => {
  const dispatch = useAppDispatch()
  const checkoutError = useCheckoutError()
  const confirmCheckout = useConfirmCheckout()
  const isApplePaylater = useIsApplePaylater()
  const { finaliseCheckout } = useFinaliseApplePaylaterCheckout()
  const enabled = {
    SMS: true,
    EMAIL: checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequired,
  }
  const initialMethod = useInitial2faMethod()
  const autoSendCode = useAutoSend2fa()
  const orderToken = useCheckoutToken()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const topazTraceId = useTraceId()

  const sendCode = async (method: VerificationMethod): Promise<void> => {
    try {
      unwrapResult(await dispatch(get2faCode({ sendBy: method })))
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(SECURITY_2FA_ERRORS[errorCode])
    }
  }

  const finaliseApplePaylaterAfter2fa = async (): Promise<VerificationFlowRoute> => {
    dispatch(resetCheckoutError())
    try {
      let declineOccurred = false
      await finaliseCheckout({
        onFailure: (errorCode) => {
          declineOccurred = true
          dispatch(setCheckoutError({ error: errorCode }))
        },
      })
      if (declineOccurred) {
        return { route: Route.TERMINAL_ERROR }
      }
      return { triggerProcessingModal: true }
    } catch {
      return { route: Route.TERMINAL_ERROR }
    }
  }

  const checkCode = async (code: string): Promise<VerificationFlowRoute> => {
    const paymentMethodId = getPaymentMethodId(selectedPaymentMethod)

    try {
      unwrapResult(
        await dispatch(
          check2faCode({
            code,
            riskExtra: {
              orderToken,
              checkpoint: 'CHECKOUT_CONFIRM',
              topazTraceId,
              paymentMethodId,
              paymentMethodType: selectedPaymentMethod?.type as PaymentMethodType,
            },
          })
        )
      )

      if (isApplePaylater) {
        return finaliseApplePaylaterAfter2fa()
      }

      try {
        const confirmResponse = await confirmCheckout()
        return confirmResponse.status === 'FAILURE' ? { route: Route.TERMINAL_ERROR } : { triggerProcessingModal: true }
      } catch (e) {
        return { route: Route.TERMINAL_ERROR }
      }
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(SECURITY_2FA_ERRORS[errorCode])
    }
  }
  return {
    enabled,
    sendCode,
    checkCode,
    autoSendCode,
    initialMethod,
    context: VerificationFlowContext.Security,
    allowLogout: true,
    revealMobile: false,
    prefetchRoutes: [],
  }
}

export const useLogLoginAttemptEvent = ({ is2fa }: LoginAttemptEventParams): ((loginMethod: LoginMethodTypes, isSuccessful: boolean) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { EMAIL } = useEnabled2faMethods()
  const { uuid } = useConsumerSelector()

  return (loginMethod: LoginMethodTypes, isSuccessful: boolean): void => {
    logEvent(TrackingEvent.LOGIN_ATTEMPTED, {
      loginAttemptChannel: 'Web Login',
      verificationApproach: loginMethod,
      consumerDetails: uuid,
      loginAttemptResult: is2fa ? '2fa' : isSuccessful,
      isEmail2faOptionAvailable: EMAIL,
      ...(is2fa && { _2faResult: isSuccessful }),
    })
  }
}

export const useEfaFlow = (): VerificationFlow => {
  const dispatch = useAppDispatch()

  const sendCode = async (_: VerificationMethod): Promise<void> => {
    try {
      const { efaCheckStatus } = unwrapResult(await dispatch(getEfaCode()))
      if (efaCheckStatus === EFA_CHECK_STATUS.EFA_CHECK_NOT_REQUIRED) {
        throw new VerificationError(VerificationErrorCode.EfaCheckNotRequired)
      }
    } catch (e) {
      if (VerificationError.is(e)) {
        throw e
      }
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(EFA_ERRORS[errorCode])
    }
  }

  const checkCode = async (code: string): Promise<{ route: Route }> => {
    try {
      unwrapResult(await dispatch(verifyEfaCode({ code })))
      dispatch(setEFAFinished())
      return { route: Route.SUMMARY }
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(EFA_ERRORS[errorCode])
    }
  }

  const enabled = {
    SMS: true,
    EMAIL: false,
  }

  return {
    enabled,
    autoSendCode: true,
    sendCode,
    checkCode,
    context: VerificationFlowContext.Efa,
    allowLogout: false,
    revealMobile: false,
    prefetchRoutes: [],
  }
}

export const {
  useCashQuery,
  endpoints: { cash },
} = authService
